import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Stack } from '@mui/material';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import * as yup from 'yup';
import {
    getPendingApplication,
    sendApprovalForPendingApplication,
    sendRejectionForPendingApplication,
    getAgentInfoFromPendingApplication,
    getPendingApplicationStatus,
} from 'api/enrollment';
import MuiButton from 'components/Common/Button/MuiButton';
import {
    TextFieldWithController,
    DatePickerWithController,
} from 'components/Common/Form/Input';
import { PageLoadingCircle } from 'components/Common/Loading';
import ReviewApplicantInfo from 'components/EnrollmentV2/forms/Review/ReviewApplicantInfo';
import ReviewEligibility from 'components/EnrollmentV2/forms/Review/ReviewEligibility';
import ReviewEmergencyContact from 'components/EnrollmentV2/forms/Review/ReviewEmergencyContact';
import ReviewMedicare from 'components/EnrollmentV2/forms/Review/ReviewMedicareInfo';
import ReviewPayment from 'components/EnrollmentV2/forms/Review/ReviewPayment';
import ReviewPCP from 'components/EnrollmentV2/forms/Review/ReviewPCP';
import ReviewSNP from 'components/EnrollmentV2/forms/Review/ReviewSNP';
import './index.scss';
import {
    FeedbackType,
    PENDING_APP_STATUS,
} from 'enums/Enrollment/PendingApplicationStatus';
import { VERIFICATION_STATUS } from 'enums/MemberVerification';
import FeedbackView from './Enrollment/FeedbackView';
import PageHeader from './Common/PageHeader';
import ConfirmationEnroller from './Enrollment/ConfirmationEnroller';
import ConfirmationHeader from './Enrollment/ConfirmationHeader';
import ConfirmationPlanInfo from './Enrollment/ConfirmationPlanInfo';
import VerificationScreen from './Verification/VerificationScreen';

const schema = yup.object({
    reviewBeneficiarySignature: yup.string().required('Required'),
    reviewBeneficiarySignatureDate: yup.string().required('Required'),
});

type EnrollmentReviewFormSchema = yup.InferType<typeof schema>;

const ImportantInformationText = () => (
    <>
        <ul>
            <li>
                I must keep Hospital (Part A) or Medical (Part B) to stay in
                Alignment Health Plan.
            </li>
            <li>
                By joining this Medicare Advantage Plan or Medicare Prescription
                Drug Plan, I acknowledge that Alignment Health Plan will share
                my information with Medicare, who may use it to track my
                enrollment, to make payments, and for other purposes allowed by
                Federal law that authorize the collection of this information
                (see Privacy Act Statement below).
            </li>
            <li>
                Your response to this form is voluntary. However, failure to
                respond may affect enrollment in the plan.
            </li>
            <li>
                The information on this enrollment form is correct to the best
                of my knowledge. I understand that if I intentionally provide
                false information on this form, I will be disenrolled from the
                plan.
            </li>
            <li>
                I understand that people with Medicare are generally not covered
                under Medicare while out of the country, except for limited
                coverage near the U.S. border.
            </li>
            <li>
                I understand that when my Alignment Health Plan coverage begins,
                I must get all of my medical and prescription drug benefits from
                Alignment Health Plan. Benefits and services provided by
                Alignment Health Plan and contained in my Alignment Health Plan
                “Evidence of Coverage” document (also known as a member contract
                or subscriber agreement) will be covered. Neither Medicare nor
                Alignment Health Plan will pay for benefits or services that are
                not covered.
            </li>
            <li>
                I understand that my signature (or the signature of the person
                legally authorized to act on my behalf) on this application
                means that I have read and understand the contents of this
                application. If signed by an authorized representative (as
                described above), this signature certifies that:
                <ol>
                    <li>
                        This person is authorized under State law to complete
                        this enrollment, and
                    </li>
                    <li>
                        Documentation of this authority is available upon
                        request by Medicare.
                    </li>
                </ol>
            </li>
        </ul>
        <p className="privacy-statement">
            <strong>PRIVACY ACT STATEMENT</strong>
            <br />
            The Centers for Medicare &amp; Medicaid Services (CMS) collects
            information from Medicare plans to track beneficiary enrollment in
            Medicare Advantage (MA) or Prescription Drug Plans (PDP), improve
            care, and for the payment of Medicare benefits. Sections 1851 and
            1860D-1 of the Social Security Act and 42 CFR §§ 422.50, 422.60
            authorize the collection of this information. CMS may use, disclose
            and exchange enrollment data from Medicare beneficiaries as
            specified in the System of Records Notice (SORN) “Medicare Advantage
            Prescription Drug (MARx)”, System No. 09-70-0588. Your response to
            this form is voluntary. However, failure to respond may affect
            enrollment in the plan.
        </p>
    </>
);

const EnrollmentReview = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [application, setApplication] = useState<any>({});
    const [status, setStatus] = useState<string>();
    const [agentInfo, setAgentInfo] = useState<any>();
    const [verificationStatus, setVerificationStatus] = useState<string>();
    const { clientId } = useParams();
    const homePhone = application?.page4?.sub2?.primaryPhone;
    const cellPhone = application?.page4?.sub2?.cellPhone;
    const phoneNumberList = [homePhone, cellPhone].filter(
        (phoneNumber) => !isEmpty(phoneNumber)
    );

    const onSNPWorkflow =
        application?.page2?.planSummary.isSNPPrequalFormRequired;

    const sendApplicationFeedback = (status: FeedbackType) => {
        if (status === FeedbackType.ACCEPTED) {
            handleSubmit((formData) => {
                setIsLoading(true);
                sendApprovalForPendingApplication(
                    clientId,
                    formData.reviewBeneficiarySignature,
                    formData.reviewBeneficiarySignatureDate
                ).then((response) => {
                    if (response) {
                        setStatus(PENDING_APP_STATUS.APPROVED);
                    } else {
                        getPendingApplicationStatus(clientId ?? '').then(
                            (response) => {
                                setStatus(
                                    response.status ??
                                        PENDING_APP_STATUS.UNIDENTIFIED
                                );
                            }
                        );
                    }
                    setIsLoading(false);
                });
            })();
        } else if (status === FeedbackType.REJECTED) {
            setIsLoading(true);
            sendRejectionForPendingApplication(clientId).then((response) => {
                if (response) {
                    setStatus(PENDING_APP_STATUS.REJECTED);
                } else {
                    getPendingApplicationStatus(clientId ?? '').then(
                        (response) => {
                            setStatus(
                                response.status ??
                                    PENDING_APP_STATUS.UNIDENTIFIED
                            );
                        }
                    );
                }
                setIsLoading(false);
            });
        }
    };

    const methods = useForm<EnrollmentReviewFormSchema>({
        mode: 'onSubmit',
        resolver: yupResolver(schema),
    });

    const { control, handleSubmit } = methods;

    useEffect(() => {
        setIsLoading(true);
        const pendingApplicationStatus = getPendingApplicationStatus(
            clientId ?? ''
        )
            .then((response) => {
                setStatus(response.status);
            })
            .catch(() => setStatus(PENDING_APP_STATUS.UNIDENTIFIED));

        const pendingApplicationEnrollment = getPendingApplication(clientId)
            .then((response) => {
                setApplication(response);
                getAgentInfoFromPendingApplication(response.userID)
                    .then((response) => {
                        setAgentInfo(response);
                    })
                    .catch(() => {
                        setAgentInfo({});
                    });
            })
            .catch(() => {
                setApplication({});
            });

        Promise.all([
            pendingApplicationStatus,
            pendingApplicationEnrollment,
        ]).finally(() => {
            setIsLoading(false);
        });
    }, []);

    if (isLoading) {
        return <PageLoadingCircle />;
    }

    if (status !== PENDING_APP_STATUS.PENDING) {
        return <FeedbackView status={status} />;
    }

    return (
        <div>
            <PageHeader />
            {verificationStatus === VERIFICATION_STATUS.APPROVED ? (
                <Grid container>
                    <Grid
                        item
                        xs={12}
                        margin={5}
                        justifyContent={'center'}
                        sx={{
                            padding: '1rem !important',
                            gap: '0.6rem',
                            display: 'flex',
                            flexDirection: 'column',
                            marginTop: '4rem',
                        }}
                    >
                        <div id="member-confirmation-page">
                            <Grid container justifyContent="center">
                                <Grid item xs={10}>
                                    <ConfirmationHeader
                                        application={application}
                                    />
                                    <p className="header">
                                        <strong>
                                            Review Enrollment Application
                                        </strong>
                                    </p>
                                    <ConfirmationPlanInfo
                                        application={application}
                                    />
                                    <div className="py-2">
                                        <h5 className="review-title">
                                            Primary Care Provider{' '}
                                        </h5>
                                        <ReviewPCP
                                            state={{ enrollment: application }}
                                        />
                                    </div>

                                    <div className="py-2">
                                        <h5 className="review-title">
                                            Who is completing the Enrollment
                                            Application?{' '}
                                        </h5>
                                        <ConfirmationEnroller
                                            agentInfo={agentInfo}
                                        />
                                    </div>

                                    <div className="py-2">
                                        <h5 className="review-title">
                                            Medicare Information{' '}
                                        </h5>
                                        <ReviewMedicare
                                            state={{ enrollment: application }}
                                        />
                                    </div>
                                    <div className="py-2">
                                        <h5 className="review-title">
                                            Additional Information{' '}
                                        </h5>
                                        <ReviewEligibility
                                            state={{ enrollment: application }}
                                        />
                                    </div>
                                    <div className="py-2">
                                        <h5 className="review-title">
                                            Applicant Information{' '}
                                        </h5>
                                        <ReviewApplicantInfo
                                            state={{ enrollment: application }}
                                        />
                                    </div>
                                    <div className="py-2">
                                        <h5 className="review-title">
                                            Emergency Contact
                                        </h5>
                                        <ReviewEmergencyContact
                                            state={{ enrollment: application }}
                                        />
                                    </div>
                                    <div className="py-2">
                                        <h5 className="review-title">
                                            Payment Option
                                        </h5>
                                        <ReviewPayment
                                            state={{ enrollment: application }}
                                        />
                                    </div>

                                    {onSNPWorkflow && (
                                        <div className="py-2">
                                            <h5 className="review-title">
                                                SNP Questions
                                            </h5>
                                            <ReviewSNP
                                                state={{
                                                    enrollment: application,
                                                }}
                                            />
                                        </div>
                                    )}

                                    <div className="text-start">
                                        <div className="important-text-container">
                                            <p>
                                                <strong>
                                                    IMPORTANT: Read and sign
                                                    below:
                                                    <br />{' '}
                                                </strong>
                                            </p>

                                            <ImportantInformationText />
                                        </div>
                                        <div>
                                            <FormProvider {...methods}>
                                                <form>
                                                    <h5 className="enrollment-title">
                                                        Electronic Signature
                                                        Agreement
                                                    </h5>
                                                    <p>
                                                        By typing your name
                                                        below, you are
                                                        electronically signing
                                                        this Agreement. You
                                                        agree that your typed
                                                        name constitutes your
                                                        legal signature and is
                                                        the equivalent of your
                                                        manual signature on this
                                                        Agreement.
                                                    </p>
                                                    <div className="row">
                                                        <div className="col-lg-8 ps-2 py-3">
                                                            <TextFieldWithController
                                                                ref={undefined}
                                                                {...{
                                                                    name: 'reviewBeneficiarySignature',
                                                                    label: 'Signature of Beneficiary or Authorized Representative',
                                                                    placeholder:
                                                                        'Signature of Beneficiary or Authorized Representative',
                                                                    control:
                                                                        control,
                                                                    required: true,
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="col-lg-4 ps-2 py-3">
                                                            <DatePickerWithController
                                                                ref={undefined}
                                                                {...{
                                                                    name: 'reviewBeneficiarySignatureDate',
                                                                    disabled: true,
                                                                    label: 'Signature Date',
                                                                    defaultValue:
                                                                        DateTime.now().toFormat(
                                                                            'yyyy-MM-dd'
                                                                        ),
                                                                    value: DateTime.now().toFormat(
                                                                        'yyyy-MM-dd'
                                                                    ),
                                                                    control:
                                                                        control,
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <h6>
                                                        Agent Signature
                                                        Agreement
                                                    </h6>

                                                    <div className="row">
                                                        <div className="col-lg-8 ps-2 py-3">
                                                            <TextFieldWithController
                                                                ref={undefined}
                                                                {...{
                                                                    defaultValue:
                                                                        application
                                                                            .page5
                                                                            ?.reviewAgentSignature,
                                                                    name: 'enrollment.page5.reviewAgentSignature',
                                                                    label: 'Agent Signature',
                                                                    control:
                                                                        control,
                                                                    disabled: true,
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="col-lg-4 ps-2 py-3">
                                                            <DatePickerWithController
                                                                ref={undefined}
                                                                {...{
                                                                    defaultValue:
                                                                        DateTime.now().toFormat(
                                                                            'yyyy-MM-dd'
                                                                        ),
                                                                    value: DateTime.now().toFormat(
                                                                        'yyyy-MM-dd'
                                                                    ),
                                                                    name: 'enrollment.page5.reviewAgentSignatureDate',
                                                                    label: 'Signature Date',
                                                                    disabled: true,
                                                                    control:
                                                                        control,
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </form>
                                            </FormProvider>
                                        </div>
                                    </div>
                                </Grid>
                            </Grid>
                            <Stack
                                justifySelf="center"
                                alignItems="center"
                                justifyContent="space-around"
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                width="50%"
                                paddingY={2}
                            >
                                <MuiButton
                                    variant="outlined"
                                    disabled={isLoading}
                                    onClick={() => {
                                        sendApplicationFeedback(
                                            FeedbackType.REJECTED
                                        );
                                    }}
                                >
                                    Reject
                                </MuiButton>
                                <MuiButton
                                    variant="outlined"
                                    disabled={isLoading}
                                    type="submit"
                                    onClick={() =>
                                        sendApplicationFeedback(
                                            FeedbackType.ACCEPTED
                                        )
                                    }
                                >
                                    Accept & Enroll
                                </MuiButton>
                            </Stack>
                        </div>
                    </Grid>
                </Grid>
            ) : (
                <Grid container>
                    <Grid
                        item
                        xs={12}
                        justifyContent={'center'}
                        sx={{
                            padding: '1rem !important',
                            gap: '0.6rem',
                            display: 'flex',
                            flexDirection: 'column',
                            marginTop: '4rem',
                        }}
                    >
                        <VerificationScreen
                            setVerificationStatus={setVerificationStatus}
                            phoneNumberList={phoneNumberList}
                        />
                    </Grid>
                </Grid>
            )}
        </div>
    );
};

export default EnrollmentReview;
